/**
 * Fades lazily-loaded images in once they have actually decoded, rather than
 * letting them pop in abruptly mid-scroll.
 *
 * The `js-image-fade` class is added to <html> by this script, and the opacity
 * rule in base.css is scoped under it. So if the script is blocked or throws,
 * images simply render normally instead of being stuck invisible — the source
 * applied `opacity: 0` unconditionally and had no such fallback.
 *
 * Ported from the inline IIFE in ../foamico-main/index.html.
 */
(() => {
  const root = document.documentElement;
  root.classList.add('js-image-fade');

  const mark = (img) => img.classList.add('is-loaded');

  const watch = (img) => {
    if (img.complete && img.naturalWidth > 0) {
      mark(img);
      return;
    }
    img.addEventListener('load', () => mark(img), { once: true });
    // A broken image would otherwise stay at opacity 0 forever.
    img.addEventListener('error', () => mark(img), { once: true });
  };

  const scan = (scope = document) => {
    for (const img of scope.querySelectorAll('img[loading="lazy"]:not(.is-loaded)')) watch(img);
  };

  scan();

  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  /**
   * And everything that arrives after this point.
   *
   * The opacity rule does not care how an image got onto the page — an
   * unwatched one stays invisible forever. So every fetch-and-swap in the
   * theme had to remember to deal with that itself: filtering, sorting,
   * recommendations, and then the search suggestions, whose thumbnails
   * shipped blank because that fourth site was written before the rule was
   * understood.
   *
   * Watching the document is the version that cannot be forgotten. The
   * alternative — a line of boilerplate at each swap site — has now been
   * forgotten once, which is once more than the argument for it survives.
   */
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches('img[loading="lazy"]')) watch(node);
        else scan(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
